/**
 * All Day Section
 *
 * Expandable section above the time grid for all-day events.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { isSameDay } from 'date-fns';
import type { ScheduleItem } from '@/hooks/useScheduler';

interface AllDaySectionProps {
  days: Date[];
  items: ScheduleItem[];
  onItemClick?: (item: ScheduleItem) => void;
}

export function AllDaySection({ days, items, onItemClick }: AllDaySectionProps) {
  const [expanded, setExpanded] = useState(true);

  if (items.length === 0) return null;

  // Group items by day
  const itemsByDay = new Map<string, ScheduleItem[]>();
  for (const day of days) {
    const key = day.toISOString();
    itemsByDay.set(
      key,
      items.filter((item) => isSameDay(new Date(item.startTime), day))
    );
  }

  return (
    <div className="border-b border-border-primary">
      <div className="flex">
        {/* Gutter area */}
        <div className="w-14 shrink-0 border-r border-border-primary flex items-center justify-end pr-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-text-quaternary hover:text-text-tertiary transition-colors"
          >
            {expanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
          </button>
        </div>

        {/* Day columns */}
        {expanded && days.map((day) => {
          const dayItems = itemsByDay.get(day.toISOString()) || [];
          return (
            <div
              key={day.toISOString()}
              className="flex-1 border-r border-border-primary last:border-r-0 p-1 min-h-[28px]"
            >
              {dayItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => onItemClick?.(item)}
                  className={clsx(
                    'px-1.5 py-0.5 rounded text-[9px] font-medium truncate cursor-pointer mb-0.5',
                    item.type === 'calendar_event'
                      ? 'bg-accent-violet/15 text-accent-violet/80'
                      : 'bg-accent-blue/15 text-accent-blue'
                  )}
                >
                  {item.title}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
